import { each, isNull } from 'lodash/fp';
import { ServiceOrderState } from '@mews-commander/data-access';
import { getToken } from '@optimus-web/core';
import { DENSE_LIGHT_THEME } from '@optimus-web/theme';
import { ServiceOrderState as ServerServiceOrderState } from '@mews-commander/legacy-data-structures';
import {
    getColorFromReservationTimeState,
    getStrokeColorFromReservationTimeState,
    getTextColorFromReservationTimeState,
} from '@mews-commander/utils';
import {
    LABEL_PART,
    ICON_WIDTH,
    CLASSIFICATION_WIDTH,
    CLASSIFICATION_FONT_SIZE,
    LABEL_FONT_SIZE,
    FONT_FAMILY,
    LABEL_SCALE_BREAKPOINT,
    DATE_LEGEND_HEIGHT,
    LINE_HEIGHT,
    CORNER_RADIUS,
} from '../../constants';
import { drawUseRectangle, fitText, drawIcon, drawClassification, drawText } from '../../utils/drawUtils';
import { roundTo5 } from '../../utils/mathUtils';

import { isOtherService } from '../../utils/services';
import { getLabelParts } from './getLabelParts';

export default function ResourceUse(fromTextCache: $TSFixMe, companionExtent: $TSFixMe) {
    let iconWidth = 0;
    let classificationWidth = 0;
    let classificationFontSize = 0;
    let fontSize = 0;
    let labelPadding = 0;
    let iconPadding = 0;
    let textPadding = 0;
    let largeTextPadding = 0;
    let lineHeight = 0;
    let cornerRadius = 0;

    return {
        render({
            ctx,
            xAxis,
            yAxis,
            scale,
            selectedResourceUse,
            uses,
            viewport,
            resourceLegendWidth,
            timelineServiceId,
        }: $TSFixMe) {
            iconWidth = ICON_WIDTH * scale;
            classificationWidth = CLASSIFICATION_WIDTH * scale;
            classificationFontSize = CLASSIFICATION_FONT_SIZE * scale;
            fontSize = LABEL_FONT_SIZE * scale;
            labelPadding = 19 * scale;
            iconPadding = 6 * scale;
            textPadding = 2 * scale;
            largeTextPadding = 4 * textPadding;
            lineHeight = LINE_HEIGHT * scale;
            cornerRadius = CORNER_RADIUS / scale;

            ctx.font = `${fontSize}px ${FONT_FAMILY}`;
            const { width: mWidth } = ctx.measureText('m');

            each((use) => {
                // @ts-expect-error $TSFixMeError: Property 'serviceId' does not exist on type 'unknown'.
                const { serviceId } = use;
                if (isNull(selectedResourceUse) || (use as $TSFixMe).id !== selectedResourceUse.id) {
                    const isGroupSelected =
                        selectedResourceUse && selectedResourceUse.groupId === (use as $TSFixMe).groupId;
                    const isOtherServiceUse = isOtherService(serviceId, timelineServiceId);
                    drawUse({
                        ctx,
                        xAxis,
                        yAxis,
                        scale,
                        viewport,
                        use,
                        resourceLegendWidth,
                        isGroupSelected,
                        hasSelected: !isNull(selectedResourceUse),
                        isOtherServiceUse,
                        mWidth,
                    });
                }
            })(uses);

            if (!isNull(selectedResourceUse)) {
                const { serviceId } = selectedResourceUse;
                const isOtherServiceUse = isOtherService(serviceId, timelineServiceId);
                drawUse({
                    ctx,
                    xAxis,
                    yAxis,
                    scale,
                    viewport,
                    resourceLegendWidth,
                    use: selectedResourceUse,
                    isSelected: true,
                    hasSelected: true,
                    isOtherServiceUse,
                    mWidth,
                });
            }
        },
    };

    function drawLabel(
        ctx: $TSFixMe,
        parts: $TSFixMe,
        tx1: $TSFixMe,
        tx2: $TSFixMe,
        ty: number,
        textColor: $TSFixMe,
        mWidth: $TSFixMe,
        scale: $TSFixMe,
        isSelected: $TSFixMe
    ) {
        let x1 = tx1;
        let x2 = tx2;
        each(({ value, type }) => {
            const availableResource = Math.floor(x2 - x1);
            switch (type) {
                case LABEL_PART.ICON: {
                    if (availableResource > iconWidth) {
                        drawIcon(ctx, x1, ty + iconPadding, value, scale, textColor);
                        x1 += iconWidth;
                        break;
                    }
                    return false;
                }
                case LABEL_PART.CUSTOMER_CLASSIFICATION: {
                    if (availableResource > classificationWidth) {
                        drawClassification(
                            ctx,
                            x2,
                            ty + iconPadding,
                            classificationWidth,
                            textPadding,
                            value,
                            classificationFontSize,
                            textColor,
                            scale
                        );
                        x2 -= classificationWidth + textPadding;
                        break;
                    }
                    return false;
                }
                default: {
                    if (availableResource < mWidth) {
                        // not space for any text
                        return false;
                    }

                    ctx.font = `${fontSize}px ${FONT_FAMILY}`;
                    const { text, width }: { text: unknown; width: number } =
                        value?.length && value.length * mWidth > availableResource
                            ? fromTextCache(value, `${roundTo5(Math.abs(availableResource / scale))}`, () =>
                                  fitText(ctx, value, availableResource)
                              )
                            : { text: value, width: availableResource };

                    if (text) {
                        drawText(ctx, text, x1, ty + labelPadding, isSelected, fontSize, textColor);
                        x1 += width + 2 * textPadding;
                    } else {
                        return false;
                    }
                }
            }
        })(parts);
    }

    function drawUse({
        ctx,
        xAxis,
        yAxis,
        scale,
        use,
        viewport,
        resourceLegendWidth,
        isSelected = false,
        isGroupSelected = false,
        hasSelected = false,
        isOtherServiceUse = false,
        mWidth = 0,
    }: $TSFixMe) {
        const { minX, minY, maxX, maxY, displaceY, timeState, type, state, resource, isReservation, options } = use;
        if (!resource) {
            return;
        }

        const topSpacing = 3.5;
        const bottomSpacing = 2.5;

        const x1 = (xAxis(minX) as number) + 1.5;
        const x2 = (xAxis(maxX) as number) - 1.5;
        const y1 = (yAxis((minY as number) + (displaceY as number)) as number) + DATE_LEGEND_HEIGHT + topSpacing;
        const y2 = (yAxis((maxY as number) + (displaceY as number)) as number) + DATE_LEGEND_HEIGHT - bottomSpacing;

        const bgColorNew = getColorFromReservationTimeState(timeState, {
            type,
            companionExtent,
            ...options,
        });
        const strokeColor = getStrokeColorFromReservationTimeState(timeState, {
            type,
            companionExtent,
            ...options,
        });
        const defaultTextColor = getToken('paletteBrandNight900')({ theme: DENSE_LIGHT_THEME });
        const textColor = isSelected
            ? getTextColorFromReservationTimeState(timeState, { type, companionExtent, ...options })
            : defaultTextColor;
        const dashed = state === ServerServiceOrderState.Optional.value || state === ServiceOrderState.Optional;

        const isInForeground = ((isSelected || isGroupSelected) && hasSelected) || !hasSelected;
        ctx.globalAlpha =
            (!isSelected && !isOtherServiceUse && !isInForeground) || (isOtherServiceUse && !isSelected) ? 0.4 : 1;

        drawUseRectangle({
            ctx,
            x1,
            x2,
            y1,
            y2,
            radius: cornerRadius,
            bgColor: bgColorNew[isSelected ? 'active' : 'default'],
            strokeColor,
            bgAlpha: 1,
            dashed,
        });

        if (scale > LABEL_SCALE_BREAKPOINT) {
            // move label into viewport if possible
            const labelX1 =
                Math.max(x1, (xAxis(viewport.minX) as number) + 1.5 + (resourceLegendWidth as number)) +
                largeTextPadding;
            const labelX2 = Math.min(x2, xAxis(viewport.maxX) - 1.5) - largeTextPadding;

            const viewportTop = (yAxis(viewport.minY) as number) + DATE_LEGEND_HEIGHT + 1.5;
            let labelY1 = y1;
            if (viewportTop > y1) {
                labelY1 = viewportTop;
            }

            if (viewportTop > y2 - lineHeight) {
                labelY1 = y2 - lineHeight + topSpacing + bottomSpacing;
            }

            const labelParts = getLabelParts({ isReservation, isOtherServiceUse, use });
            drawLabel(ctx, labelParts, labelX1, labelX2, labelY1, textColor, mWidth, scale, isSelected);
        }
        ctx.globalAlpha = 1;
    }
}
